/**
 * Models tab (TMMPDM-0098, TMMAPI-0165 section A). One read for every tier. Each tier is an
 * effective-dated setting whose one field is the model. The missing banner is `missingCurrencies`
 * alone. TMMPDM-0103: one tier per tab — the parent names the tier, the tab names it, so there is
 * no tier h2. Each tab visit reads once. A tier the server doesn't send says so; any other code is
 * not shown. TMMPDM-0104 (TMMAPI-0171): the Model options are `modelsByTier[tier]`, as sent; an
 * empty list says so and leaves Schedule a change… off. Effort is a required select, prefilled
 * from the current row or the tier's documented default, and always sent.
 * TMMPDM-0105 (TMMAPI-0172 F.16–17): each visit also reads the catalogue, so the dialog knows the
 * chosen model's row (a `modelId` lookup, a display join). The Effort options are that row's
 * `efforts` alone, in the enum's order; without `efforts` only the tier default, with a hint to
 * verify. The deprecation chip beside a model is the row's `lifecycle` alone.
 * TMMPDM-0106 (tmm-api, 2026-10-13): `efforts: []` means Anthropic reports no effort level for the
 * model, so it can't be assigned. Its option is listed but disabled, the dialog never starts on it
 * (Effort gives way to a line and Schedule stays off until another model is picked), and a Current
 * or Scheduled row naming it carries a warning with a Schedule a change… link. Each comes from that
 * row's `efforts` alone.
 * TMMPDM-0108 (TMMAPI-0174, 0176 item 11): `structuredOutputs` follows the same three states. `false`
 * disables the option (with the reason), skips the prefill and warns beside an existing assignment;
 * unknown (absent) leaves the option enabled with a note. A 400 `fields.model` picks its words from
 * the chosen row alone: retired, no structured output, unverified (with a Verify button that
 * checks the model and re-reads in place, so the dialog keeps its selections), else not on the tier.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Link, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { EmptyState, ErrorState } from '@/components/page';
import { EffectiveDatedSettings } from '@/components/settings/EffectiveDatedSettings';
import { useCopy } from '@/hooks/useCopy';
import { adminApi } from '@/api/tmm/admin';
import {
  AI_EFFORTS,
  type AiEffort,
  type AiModelAssignmentTier,
  type AiModelAssignmentWrite,
  type AiModelAssignmentsView,
  type AiModelCatalogRow,
} from '@/api/tmm/types';
import type { SettingField } from '@/components/settings/settingFields';
import { LifecycleChip } from './LifecycleChip';
import { effortLabel, verifyFailure } from './modelCatalogCopy';

export type AssignmentTierCode = 'FAST' | 'DEEP';

/** The server's documented default effort per tier (TMMAPI-0171). */
const DEFAULT_EFFORT: Record<AssignmentTierCode, AiEffort> = { FAST: 'LOW', DEEP: 'MEDIUM' };

function isEffort(value: unknown): value is AiEffort {
  return typeof value === 'string' && (AI_EFFORTS as readonly string[]).includes(value);
}

/** Anthropic reports no effort level for the model: `efforts` is sent and empty (TMMPDM-0106). */
function takesNoEffort(row: AiModelCatalogRow | undefined): boolean {
  return row?.efforts !== undefined && row.efforts.length === 0;
}

/** Anthropic says the model takes no structured output: `structuredOutputs` is sent and false. */
function noStructuredOutput(row: AiModelCatalogRow | undefined): boolean {
  return row?.structuredOutputs === false;
}

/** The catalogue knows the model but not whether it takes structured output (absent, TMMAPI-0174). */
function structuredUnknown(row: AiModelCatalogRow | undefined): boolean {
  return row !== undefined && row.structuredOutputs === undefined;
}

/**
 * The efforts a model can be assigned with (TMMAPI-0172 F.17): its `efforts`, in the enum's order;
 * without them (not verified since 0172, or no model chosen) only the tier's default.
 */
function effortOptions(code: AssignmentTierCode, row: AiModelCatalogRow | undefined): AiEffort[] {
  const efforts = row?.efforts;
  if (efforts === undefined) return [DEFAULT_EFFORT[code]];
  return AI_EFFORTS.filter((effort) => efforts.includes(effort));
}

function TierBlock({
  code,
  tier,
  models,
  catalogue,
  onReload,
  onRefresh,
}: {
  code: AssignmentTierCode;
  tier: AiModelAssignmentTier;
  models: readonly string[];
  /** The catalogue's rows by `modelId`. */
  catalogue: ReadonlyMap<string, AiModelCatalogRow>;
  onReload: () => void;
  /** Re-read the catalogue and assignments without leaving the page's current view. */
  onRefresh: () => Promise<void>;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const title =
    code === 'FAST' ? t('ai.models.fast', 'Fast tier') : t('ai.models.deep', 'Deep tier');
  const chosen = (values: Readonly<Record<string, string>>) => catalogue.get(values.model ?? '');
  const noEffortReason = t(
    'ai.catalog.noEffortReason',
    "Anthropic reports no effort support; this model can't be assigned.",
  );
  const noStructuredReason = t(
    'ai.catalog.noStructuredReason',
    "Anthropic says this model doesn't take structured outputs; it can't be assigned.",
  );
  /** Verify the chosen model (the 400's "Verify this model first."); null when it worked. */
  const verifyChosen = async (row: AiModelCatalogRow): Promise<string | null> => {
    try {
      await adminApi.verifyAiModel(row.id);
    } catch (err) {
      const { refusal, notFound } = verifyFailure(t, err);
      if (notFound) await onRefresh();
      return refusal.reason ? `${refusal.text} (${refusal.reason})` : refusal.text;
    }
    await onRefresh();
    return null;
  };
  const fields: SettingField[] = [
    {
      key: 'model',
      label: t('ai.models.field', 'Model'),
      help: t('ai.models.fieldHelp', 'The model this tier calls.'),
      kind: 'select',
      options: models,
      rejectedFor: (values) => {
        const row = chosen(values);
        if (row?.lifecycle === 'retired') {
          return t('ai.models.modelRetired', 'Anthropic has retired this model. Choose another.');
        }
        if (noStructuredOutput(row)) {
          return t(
            'ai.models.modelNoStructured',
            "This model doesn't take structured outputs. Choose another.",
          );
        }
        if (structuredUnknown(row)) {
          return t('ai.models.modelVerifyFirst', 'Verify this model first.');
        }
        return t(
          'ai.models.notOnTier',
          "That model isn't allowed on this tier. Tick the tier on the Models tab first.",
        );
      },
      errorActionFor: (values) => {
        const row = chosen(values);
        if (row === undefined || row.lifecycle === 'retired' || !structuredUnknown(row)) {
          return null;
        }
        return {
          label: t('ai.catalog.verify', 'Verify'),
          ariaLabel: t('ai.models.verifyModel', 'Verify {{model}}', { model: row.modelId }),
          busyLabel: t('ai.catalog.checking', 'Checking with Anthropic…'),
          run: () => verifyChosen(row),
        };
      },
      optionNote: (value) =>
        structuredUnknown(catalogue.get(value))
          ? t(
              'ai.models.notVerifiedStructured',
              'Not verified for structured output. Verify it before assigning.',
            )
          : null,
      adornment: (value) => <LifecycleChip row={catalogue.get(value)} locale={locale} />,
      unavailable: (value) => {
        const row = catalogue.get(value);
        if (takesNoEffort(row)) return noEffortReason;
        return noStructuredOutput(row) ? noStructuredReason : null;
      },
      warningFor: (value) => {
        const row = catalogue.get(value);
        if (takesNoEffort(row)) {
          return t(
            'ai.models.noEffortWarning',
            'Anthropic no longer reports effort support for this model; schedule a different model.',
          );
        }
        return noStructuredOutput(row)
          ? t(
              'ai.models.noStructuredWarning',
              'Anthropic says this model no longer takes structured outputs; schedule a different model.',
            )
          : null;
      },
    },
    {
      key: 'effort',
      label: t('ai.models.effort', 'Effort'),
      help: t(
        'ai.models.effortHelp',
        'How much the model thinks before answering. Higher effort costs more and can run out of room for the answer. Fast defaults to Low, Deep to Medium.',
      ),
      kind: 'select',
      options: AI_EFFORTS,
      optionLabels: Object.fromEntries(
        AI_EFFORTS.map((effort) => [effort, effortLabel(t, effort)]),
      ),
      initial: DEFAULT_EFFORT[code],
      optionsFor: (values) => effortOptions(code, chosen(values)),
      hintFor: (values) => {
        const row = chosen(values);
        if (row === undefined || row.efforts !== undefined) return null;
        return (
          <>
            {t(
              'ai.models.effortVerifyHint',
              'Verify this model on the Models tab to choose another effort.',
            )}{' '}
            <Link component={RouterLink} to="?tab=models">
              {t('ai.models.openModels', 'Open the Models tab')}
            </Link>
          </>
        );
      },
      // A current model that takes no effort starts unchosen, and Effort waits for another pick.
      replacedFor: (values) =>
        takesNoEffort(chosen(values)) ||
        ((values.model ?? '') === '' && takesNoEffort(catalogue.get(tier.current?.model ?? '')))
          ? t('ai.models.effortNone', 'This model takes no effort level. Choose a different model.')
          : null,
      rejectedFor: (values) =>
        takesNoEffort(chosen(values))
          ? { key: 'model', text: noEffortReason }
          : chosen(values)?.efforts === undefined
            ? t(
                'ai.models.effortVerify',
                'Verify the model on the Models tab to choose another effort.',
              )
            : t('ai.models.effortNotTaken', "This model doesn't take that effort."),
    },
  ];
  const priced = tier.pricedCurrencies;
  const missing = tier.missingCurrencies;
  const modelName = tier.current?.model;
  const model = modelName !== undefined && modelName.trim() !== '' ? modelName.trim() : '—';
  let banner: ReactNode;
  if (missing.length > 0) {
    banner = (
      <Alert severity="warning">
        {t(
          'ai.models.missing',
          '{{model}} has no price in {{currencies}}, so AI calls in those currencies are refused.',
          { model, currencies: missing.join(', ') },
        )}{' '}
        <Link component={RouterLink} to="?tab=models">
          {t('ai.models.addPrice', 'Add the price on the Models tab.')}
        </Link>
      </Alert>
    );
  }
  const afterCurrent =
    priced.length > 0 ? (
      <Typography variant="body2" sx={{ mt: 1 }}>
        {t('ai.models.priced', 'Priced in {{currencies}}.', { currencies: priced.join(', ') })}
      </Typography>
    ) : null;

  return (
    <EffectiveDatedSettings
      title={title}
      showTitle={false}
      instanceKey={code}
      fields={fields}
      supplied={{
        current: tier.current,
        scheduled: tier.scheduled,
        history: tier.history,
      }}
      onReload={onReload}
      emptyCurrent={t(
        'ai.models.emptyCurrent',
        'No model. AI on this tier is off until one is assigned.',
      )}
      {...(afterCurrent ? { afterCurrent } : {})}
      {...(banner ? { banner } : {})}
      allowSchedule={models.length > 0}
      schedule={async (body) => {
        const modelValue = typeof body.model === 'string' ? body.model : '';
        // The dialog always holds an effort, and it is always sent (an explicit choice).
        const effort = isEffort(body.effort) ? body.effort : DEFAULT_EFFORT[code];
        const write: AiModelAssignmentWrite = { tier: code, model: modelValue, effort };
        if (typeof body.effectiveFrom === 'string') write.effectiveFrom = body.effectiveFrom;
        if (typeof body.note === 'string' && body.note !== '') write.note = body.note;
        await adminApi.scheduleModelAssignment(write);
      }}
      withdraw={(id, note) => adminApi.withdrawModelAssignment(id, note)}
    />
  );
}

interface TabData {
  assignments: AiModelAssignmentsView;
  catalogue: AiModelCatalogRow[];
}

export function ModelAssignments({ tier: code }: { tier: AssignmentTierCode }) {
  const { t } = useCopy();
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<{
    key: string;
    data: TabData | null;
    error: boolean;
    failure?: unknown;
  }>({ key: '', data: null, error: false });
  const key = String(reloadToken);

  useEffect(() => {
    let cancelled = false;
    Promise.all([adminApi.getModelAssignments(), adminApi.listAiModels()])
      .then(([assignments, catalogue]) => {
        if (!cancelled) {
          setState({ key, data: { assignments, catalogue: catalogue.models }, error: false });
        }
      })
      .catch((failure: unknown) => {
        if (!cancelled) setState({ key, data: null, error: true, failure });
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  const reload = () => setReloadToken((n) => n + 1);
  /** A re-read that keeps the page, and any open dialog, as it is (the dialog's Verify). */
  const refresh = async () => {
    try {
      const [assignments, catalogue] = await Promise.all([
        adminApi.getModelAssignments(),
        adminApi.listAiModels(),
      ]);
      setState((prev) =>
        prev.key === key
          ? { key, data: { assignments, catalogue: catalogue.models }, error: false }
          : prev,
      );
    } catch {
      // The verify itself worked; the next Schedule or reload shows the current state.
    }
  };
  const current = state.key === key;
  if (!current || (state.data === null && !state.error)) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('common.loading', 'Loading')}
      </Typography>
    );
  }
  if (state.error || state.data === null) {
    return (
      <ErrorState
        error={state.failure}
        message={t('ai.models.loadError', 'Could not load model assignments.')}
        onRetry={reload}
      />
    );
  }

  const tier = state.data.assignments.tiers.find((entry) => entry.tier === code);
  const models = state.data.assignments.modelsByTier[code];
  const catalogue = new Map(state.data.catalogue.map((row) => [row.modelId, row]));
  if (tier === undefined) {
    return <EmptyState surface title={t('ai.models.tierMissing', "This tier isn't available.")} />;
  }

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        {t(
          'ai.models.caption',
          'Which model each tier calls. Changes apply to AI calls that start afterwards.',
        )}
      </Typography>
      {models.length === 0 ? (
        <EmptyState
          surface
          title={t(
            'ai.models.noneOnTier',
            'No models are allowed on this tier. Add one on the Models tab, with this tier ticked.',
          )}
          action={
            <Link component={RouterLink} to="?tab=models">
              {t('ai.models.openModels', 'Open the Models tab')}
            </Link>
          }
        />
      ) : null}
      <TierBlock
        code={code}
        tier={tier}
        models={models}
        catalogue={catalogue}
        onReload={reload}
        onRefresh={refresh}
      />
    </Stack>
  );
}
